import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import Toast from '../components/Toast';
import api from '../api';

/**
 * /admin/creator-waitlist
 *
 * Lists everyone who submitted the "interested creator" form on the
 * standalone `/creators.html` marketing page. Admins can:
 *   - Filter by status (all / new / invited / onboarded / declined)
 *   - Update status (progress a lead through the funnel)
 *   - Delete spam entries
 *   - Export the full list as CSV
 */
const STATUS_LABELS = {
  new: '🆕 New',
  invited: '✉️ Invited',
  onboarded: '✅ Onboarded',
  declined: '❌ Declined',
};
const NEXT_STATUS = { new: 'invited', invited: 'onboarded' };
const AUDIENCE_LABELS = {
  'under-1k': '<1K',
  '1k-10k': '1K – 10K',
  '10k-100k': '10K – 100K',
  '100k-1m': '100K – 1M',
  '1m+': '1M+',
};

export default function AdminCreatorWaitlist() {
  const [rows, setRows] = useState([]);
  const [byStatus, setByStatus] = useState({ new: 0, invited: 0, onboarded: 0, declined: 0 });
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/creator-waitlist');
      setRows(data.entries || []);
      setByStatus(data.by_status || {});
    } catch (ex) {
      setToast({ msg: ex?.response?.data?.detail || 'Failed to load waitlist' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (id, next) => {
    setBusy(id);
    try {
      await api.patch(`/admin/creator-waitlist/${id}`, { status: next });
      setToast({ msg: `Marked as ${next}` });
      await refresh();
    } catch (ex) {
      setToast({ msg: ex?.response?.data?.detail || 'Update failed' });
    } finally {
      setBusy(null);
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    setBusy(id);
    try {
      await api.delete(`/admin/creator-waitlist/${id}`);
      setToast({ msg: 'Deleted' });
      await refresh();
    } catch (ex) {
      setToast({ msg: ex?.response?.data?.detail || 'Delete failed' });
    } finally {
      setBusy(null);
    }
  };

  const generateInvite = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/creator-waitlist/${id}/invite`);
      if (data.already_existed) {
        setToast({ msg: `Code already exists: ${data.code} (email not resent)` });
      } else {
        setToast({ msg: `✉️ Invite ${data.code} generated & playbook emailed` });
      }
      await refresh();
    } catch (ex) {
      setToast({ msg: ex?.response?.data?.detail || 'Failed to generate invite' });
    } finally {
      setBusy(null);
    }
  };

  const copyCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setToast({ msg: `Copied ${code} to clipboard` });
    } catch {
      setToast({ msg: `Copy failed — code is ${code}` });
    }
  };

  const exportCsv = () => {
    const header = ['Name', 'Email', 'Handle', 'Platform', 'Audience', 'Notes', 'Referral', 'Status', 'Submitted'];
    const escape = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push([
        r.name, r.email, r.handle, r.primary_platform, r.audience_size,
        r.notes, r.referral, r.status, r.created_at,
      ].map(escape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `creator-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = rows.filter((r) => tab === 'all' || r.status === tab);

  return (
    <AdminLayout active="creator-waitlist">
      <div className="admin-page" data-testid="admin-creator-waitlist-page">
        <div className="admin-page-head">
          <h1 className="admin-page-title">🎨 Creator Waitlist</h1>
          <div className="admin-page-actions">
            <button className="admin-btn" onClick={exportCsv} disabled={rows.length === 0} data-testid="cw-export-csv">
              Export CSV
            </button>
            <button className="admin-btn" onClick={refresh} data-testid="cw-refresh">
              Refresh
            </button>
          </div>
        </div>

        <p className="admin-page-sub">
          Leads captured from the <code>/creators.html</code> marketing page. Progress each entry through:
          new → invited → onboarded.
        </p>

        <div className="admin-tabs" style={{ marginBottom: 16 }}>
          {['all', 'new', 'invited', 'onboarded', 'declined'].map((t) => (
            <button
              key={t}
              className={`admin-tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
              data-testid={`cw-tab-${t}`}
            >
              {t === 'all' ? 'All' : STATUS_LABELS[t]}
              {t !== 'all' && (
                <span className="admin-tab-count"> · {byStatus[t] || 0}</span>
              )}
              {t === 'all' && (
                <span className="admin-tab-count"> · {rows.length}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="admin-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty">
            No entries in this filter. Share <code>https://clanster.xyz/creators</code> to start
            capturing leads.
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="cw-table">
              <thead>
                <tr>
                  <th>Creator</th>
                  <th>Platform · Audience</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`cw-row-${r.id}`}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{r.name}</div>
                      <div className="admin-small admin-mono">{r.email}</div>
                      <div className="admin-small admin-muted">@{r.handle}</div>
                    </td>
                    <td>
                      <span className="admin-pill">{r.primary_platform}</span>{' '}
                      <span className="admin-pill">{AUDIENCE_LABELS[r.audience_size] || r.audience_size}</span>
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      <div className="admin-small" title={r.notes}>
                        {r.notes || <span className="admin-muted">—</span>}
                      </div>
                      {r.referral && (
                        <div className="admin-small admin-muted" style={{ marginTop: 4 }}>
                          via {r.referral}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`admin-status admin-status-${r.status}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td className="admin-small admin-mono">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Invite flow — matches /admin/waitlist UX. Show the
                          Generate button whenever no invite code exists yet
                          (regardless of the row's status — a Declined lead
                          can still be re-invited later); once minted, swap
                          in a Copy chip so admins can grab the code fast. */}
                      {r.invite_code ? (
                        <>
                          <span
                            className="admin-mono"
                            style={{
                              padding: '4px 8px', marginRight: 6,
                              borderRadius: 8, background: 'rgba(0,180,255,0.10)',
                              color: '#7CE1FF', fontWeight: 700, fontSize: 12,
                            }}
                            data-testid={`cw-code-${r.id}`}
                          >
                            {r.invite_code}
                          </span>
                          <button
                            className="admin-btn-sm"
                            style={{ marginRight: 6, background: 'rgba(0,180,255,0.75)', color: '#fff', fontWeight: 700 }}
                            onClick={() => copyCode(r.invite_code)}
                            data-testid={`cw-copy-${r.id}`}
                          >
                            Copy
                          </button>
                        </>
                      ) : (
                        <button
                          className="admin-btn-sm"
                          onClick={() => generateInvite(r.id)}
                          disabled={busy === r.id}
                          data-testid={`cw-invite-${r.id}`}
                          style={{
                            marginRight: 6,
                            background: 'linear-gradient(135deg,#FF5470,#E040FB)',
                            color: '#fff', fontWeight: 700,
                          }}
                          title="Generate invite code and email the Creator Playbook"
                        >
                          {busy === r.id ? 'Generating…' : '✉️ Generate invite'}
                        </button>
                      )}
                      {NEXT_STATUS[r.status] && r.status !== 'new' && (
                        <button
                          className="admin-btn-sm"
                          onClick={() => updateStatus(r.id, NEXT_STATUS[r.status])}
                          disabled={busy === r.id}
                          data-testid={`cw-progress-${r.id}`}
                          style={{ marginRight: 6 }}
                        >
                          → {NEXT_STATUS[r.status]}
                        </button>
                      )}
                      {r.status !== 'declined' && (
                        <button
                          className="admin-btn-sm"
                          onClick={() => updateStatus(r.id, 'declined')}
                          disabled={busy === r.id}
                          data-testid={`cw-decline-${r.id}`}
                          style={{ marginRight: 6 }}
                        >
                          Decline
                        </button>
                      )}
                      <button
                        className="admin-btn-sm admin-btn-danger"
                        onClick={() => removeEntry(r.id)}
                        disabled={busy === r.id}
                        data-testid={`cw-delete-${r.id}`}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <Toast msg={toast.msg} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
}
